/**
 * 场景人格详情页 - 音色 Tab
 * v10.2 新增：显示绑定的音色详情，支持试听、更换绑定、解除绑定
 * v10.3 新增：支持直接调整合成参数（语速/音调/音量），无需解绑
 * v1.7 新增：支持切换训练模型，无需解绑重新绑定
 * v10.4 新增：支持情感指令（instruction）选择
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Slider } from '@/shared/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import {
  Mic,
  Play,
  Pause,
  Link2,
  Unlink,
  Loader2,
  AlertCircle,
  Save,
  RotateCcw,
  Sparkles,
} from 'lucide-react'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService, getVoiceInstructions } from '@/modules/identity/services'
import { VoiceBindDialog } from '@/modules/identity/components/VoiceBindDialog'
import type { FacetVoiceBinding } from '@/modules/identity/types/facet'
import type { VoiceTrainingModel, VoiceInstructionsConfig, VoiceInstruction } from '@/modules/identity/types'
import { getAvailableInstructions, getInstructionName } from '@/modules/identity/types'

interface FacetVoiceTabProps {
  identityId: string
  sceneId: string
  sceneName: string
  voiceBinding: FacetVoiceBinding | null
  onBindingChange?: () => void
}

export function FacetVoiceTab({
  identityId,
  sceneId,
  sceneName,
  voiceBinding,
  onBindingChange,
}: FacetVoiceTabProps) {
  const { showSuccess, showError } = useToast()
  const audioRef = useRef<HTMLAudioElement>(null)

  // 状态
  const [bindDialogOpen, setBindDialogOpen] = useState(false)
  const [unbindDialogOpen, setUnbindDialogOpen] = useState(false)
  const [unbinding, setUnbinding] = useState(false)
  const [previewText, setPreviewText] = useState('你好，很高兴见到你！')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)

  // v10.3 新增：合成参数调整
  const [speechRate, setSpeechRate] = useState(voiceBinding?.speech_rate ?? 1.0)
  const [pitchRate, setPitchRate] = useState(voiceBinding?.pitch_rate ?? 1.0)
  const [volume, setVolume] = useState(voiceBinding?.volume ?? 50)
  const [isSaving, setIsSaving] = useState(false)

  // v1.7 新增：训练模型选择
  const [trainingModels, setTrainingModels] = useState<VoiceTrainingModel[]>([])
  const [loadingModels, setLoadingModels] = useState(false)
  const [selectedModelId, setSelectedModelId] = useState<string>(voiceBinding?.model_id ?? '')

  // v10.4 新增：情感指令
  const [instructionsConfig, setInstructionsConfig] = useState<VoiceInstructionsConfig | null>(null)
  const [instruction, setInstruction] = useState<string>(voiceBinding?.instruction ?? '')
  
  // 判断是否有修改（包括模型选择和情感指令）
  const hasChanges = voiceBinding && (
    speechRate !== voiceBinding.speech_rate ||
    pitchRate !== voiceBinding.pitch_rate ||
    volume !== voiceBinding.volume ||
    selectedModelId !== voiceBinding.model_id ||  // v1.7: 模型变更
    instruction !== (voiceBinding.instruction ?? '')  // v10.4: 指令变更
  )

  // 当 voiceBinding 变化时，重置本地状态
  useEffect(() => {
    if (voiceBinding) {
      setSpeechRate(voiceBinding.speech_rate)
      setPitchRate(voiceBinding.pitch_rate)
      setVolume(voiceBinding.volume)
      setSelectedModelId(voiceBinding.model_id)
      setInstruction(voiceBinding.instruction ?? '')  // v10.4
    }
  }, [voiceBinding])

  // v1.7: 加载训练模型列表
  useEffect(() => {
    async function loadTrainingModels() {
      if (!voiceBinding?.voice_card_id) {
        setTrainingModels([])
        return
      }

      setLoadingModels(true)
      try {
        const response = await IdentityApiService.listVoiceModels(
          identityId,
          voiceBinding.voice_card_id
        )
        setTrainingModels(response.data.items || [])
      } catch (error) {
        console.error('Failed to load training models:', error)
        setTrainingModels([])
      } finally {
        setLoadingModels(false)
      }
    }

    loadTrainingModels()
  }, [identityId, voiceBinding?.voice_card_id])

  // v10.4: 加载情感指令配置
  useEffect(() => {
    async function loadInstructionsConfig() {
      try {
        const config = await getVoiceInstructions()
        setInstructionsConfig(config)
      } catch (error) {
        console.warn('Failed to load voice instructions config:', error)
      }
    }
    loadInstructionsConfig()
  }, [])

  // v10.4: 获取当前模型支持的指令列表
  const currentTargetModel = useMemo(() => {
    // 从训练模型列表中获取当前选择的模型的 target_model
    const selectedModel = trainingModels.find(m => m.model_id === selectedModelId)
    return selectedModel?.target_model || ''
  }, [trainingModels, selectedModelId])

  const availableInstructions = useMemo(() => {
    if (!instructionsConfig || !currentTargetModel) return []
    return getAvailableInstructions(instructionsConfig, currentTargetModel)
  }, [instructionsConfig, currentTargetModel])

  // v10.4: 判断是否支持情绪类指令
  const supportsEmotion = useMemo(() => {
    if (!instructionsConfig?.cosyvoice || !currentTargetModel) return false
    const supportedTypes = instructionsConfig.cosyvoice.model_support[currentTargetModel] || []
    return supportedTypes.includes('emotion')
  }, [instructionsConfig, currentTargetModel])

  // 试听（使用当前选择的模型和调整后的参数）
  const handlePreview = useCallback(async () => {
    if (!voiceBinding || !previewText.trim()) return

    setIsPreviewing(true)
    try {
      const response = await IdentityApiService.previewVoice(
        identityId,
        voiceBinding.voice_card_id,
        {
          text: previewText,
          model_id: selectedModelId || voiceBinding.model_id,  // v1.7: 使用选择的模型
          speech_rate: speechRate,
          pitch_rate: pitchRate,
          volume: volume,
        }
      )
      setPreviewUrl(response.data.audio_url)

      // 自动播放
      if (audioRef.current) {
        audioRef.current.src = response.data.audio_url
        audioRef.current.load()
        audioRef.current.oncanplaythrough = () => {
          audioRef.current?.play().catch(err => {
            if (err.name !== 'AbortError') {
              console.error('Play failed:', err)
            }
          })
          setIsPlaying(true)
        }
      }
    } catch (error) {
      console.error('Preview failed:', error)
      showError('试听失败', '操作失败')
    } finally {
      setIsPreviewing(false)
    }
  }, [identityId, voiceBinding, previewText, selectedModelId, speechRate, pitchRate, volume, showError])

  // v10.3 / v1.7 / v10.4: 保存配置（合成参数 + 模型选择 + 情感指令）
  const handleSaveConfig = useCallback(async () => {
    if (!voiceBinding || !hasChanges) return

    setIsSaving(true)
    try {
      // v1.7 / v10.4: 支持同时更新 model_id、合成参数和情感指令
      const updateData: {
        model_id?: string
        speech_rate?: number
        pitch_rate?: number
        volume?: number
        instruction?: string
      } = {}

      // 只传递有变更的字段
      if (selectedModelId !== voiceBinding.model_id) {
        updateData.model_id = selectedModelId
      }
      if (speechRate !== voiceBinding.speech_rate) {
        updateData.speech_rate = speechRate
      }
      if (pitchRate !== voiceBinding.pitch_rate) {
        updateData.pitch_rate = pitchRate
      }
      if (volume !== voiceBinding.volume) {
        updateData.volume = volume
      }
      // v10.4: 情感指令（空字符串表示清除）
      if (instruction !== (voiceBinding.instruction ?? '')) {
        updateData.instruction = instruction
      }

      await IdentityApiService.updateVoiceBindingConfig(identityId, sceneId, updateData)
      
      const modelChanged = selectedModelId !== voiceBinding.model_id
      const instructionChanged = instruction !== (voiceBinding.instruction ?? '')
      let message = '合成参数已更新，下次对话生效'
      if (modelChanged && instructionChanged) {
        message = '模型、情感指令和参数已更新，下次对话生效'
      } else if (modelChanged) {
        message = '训练模型和参数已更新，下次对话生效'
      } else if (instructionChanged) {
        message = '情感指令和参数已更新，下次对话生效'
      }
      showSuccess(message, '保存成功')
      onBindingChange?.()  // 刷新绑定信息
    } catch (error) {
      console.error('Failed to update config:', error)
      // 检查是否是 MODEL_NOT_IN_CARD 错误
      const errorMessage = (error as Error)?.message || ''
      if (errorMessage.includes('MODEL_NOT_IN_CARD')) {
        showError('所选模型不属于当前绑定的音色卡片', '验证失败')
      } else {
        showError('保存失败', '操作失败')
      }
    } finally {
      setIsSaving(false)
    }
  }, [identityId, sceneId, voiceBinding, hasChanges, selectedModelId, speechRate, pitchRate, volume, instruction, showSuccess, showError, onBindingChange])

  // v10.3 / v1.7 / v10.4: 重置参数（包括模型选择和情感指令）
  const handleResetConfig = useCallback(() => {
    if (voiceBinding) {
      setSpeechRate(voiceBinding.speech_rate)
      setPitchRate(voiceBinding.pitch_rate)
      setVolume(voiceBinding.volume)
      setSelectedModelId(voiceBinding.model_id)
      setInstruction(voiceBinding.instruction ?? '')  // v10.4
    }
  }, [voiceBinding])

  // 切换播放
  const togglePlay = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else if (previewUrl) {
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  // 解除绑定
  const handleUnbind = useCallback(async () => {
    setUnbinding(true)
    try {
      await IdentityApiService.unbindVoiceFromFacet(identityId, sceneId)
      showSuccess('音色绑定已解除', '操作成功')
      setUnbindDialogOpen(false)
      onBindingChange?.()
    } catch (error) {
      console.error('Failed to unbind voice:', error)
      showError('解除绑定失败', '操作失败')
    } finally {
      setUnbinding(false)
    }
  }, [identityId, sceneId, showSuccess, showError, onBindingChange])

  // 绑定成功回调
  const handleBindSuccess = () => {
    onBindingChange?.()
  }

  // 未绑定状态
  if (!voiceBinding) {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mic className="h-5 w-5" />
              绑定音色信息
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">当前场景人格未绑定音色</p>
              <Button onClick={() => setBindDialogOpen(true)}>
                <Link2 className="h-4 w-4 mr-2" />
                绑定音色
              </Button>
            </div>
          </CardContent>
        </Card>

        <VoiceBindDialog
          open={bindDialogOpen}
          onOpenChange={setBindDialogOpen}
          identityId={identityId}
          sceneId={sceneId}
          sceneName={sceneName}
          onBindSuccess={handleBindSuccess}
        />
      </>
    )
  }

  // 已绑定状态
  return (
    <>
      <audio
        ref={audioRef}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5" />
            绑定音色信息
          </CardTitle>
          <CardDescription>
            该场景在 Agent Flow 运行时将使用此音色
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 基本信息 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-slate-500">音色名称</span>
              <span className="font-medium">{voiceBinding.voice_card_name}</span>
            </div>
          </div>

          {/* v1.7 新增：训练模型选择 */}
          <div className="space-y-2">
            <Label className="text-sm text-slate-500">训练模型</Label>
            {loadingModels ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                加载模型列表...
              </div>
            ) : trainingModels.length > 0 ? (
              <Select
                value={selectedModelId}
                onValueChange={setSelectedModelId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="选择训练模型" />
                </SelectTrigger>
                <SelectContent>
                  {trainingModels.map((model) => (
                    <SelectItem key={model.model_id} value={model.model_id}>
                      <div className="flex items-center gap-2">
                        <span>{model.name}</span>
                        {model.is_active && (
                          <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                            当前使用
                          </span>
                        )}
                        {model.model_id === voiceBinding.model_id && model.model_id !== selectedModelId && (
                          <span className="text-xs text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">
                            已绑定
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-sm text-slate-500">
                <span
                  className="font-mono text-xs break-all"
                  title={voiceBinding.model_id}
                >
                  {voiceBinding.model_id}
                </span>
              </div>
            )}
            {selectedModelId !== voiceBinding.model_id && (
              <p className="text-xs text-amber-600">
                ⚠️ 已选择不同的训练模型，保存后生效
              </p>
            )}
          </div>

          {/* v10.4: 情感指令选择 */}
          {availableInstructions.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-slate-500 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-500" />
                情感指令
                <span className="text-xs font-normal text-slate-400">（可选）</span>
              </Label>
              <Select 
                value={instruction || '__none__'} 
                onValueChange={(v) => setInstruction(v === '__none__' ? '' : v)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="选择情感或方言效果">
                    {instruction 
                      ? getInstructionName(instructionsConfig, instruction) || '已选择'
                      : '无（默认）'
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">无（默认）</SelectItem>
                  {/* 情绪类 */}
                  {supportsEmotion && instructionsConfig?.cosyvoice.emotion && instructionsConfig.cosyvoice.emotion.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>情绪类</SelectLabel>
                      {instructionsConfig.cosyvoice.emotion.map((item: VoiceInstruction) => (
                        <SelectItem key={item.id} value={item.instruction}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {/* 方言类 */}
                  {instructionsConfig?.cosyvoice.dialect && instructionsConfig.cosyvoice.dialect.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>方言类</SelectLabel>
                      {instructionsConfig.cosyvoice.dialect.map((item: VoiceInstruction) => (
                        <SelectItem key={item.id} value={item.instruction}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              {!supportsEmotion && (
                <p className="text-xs text-amber-600">
                  💡 当前模型仅支持方言类指令，情绪类指令需使用 Flash 模型训练
                </p>
              )}
              {instruction !== (voiceBinding.instruction ?? '') && (
                <p className="text-xs text-amber-600">
                  ⚠️ 情感指令已修改，保存后生效
                </p>
              )}
            </div>
          )}

          {/* v10.3: 可调整的合成参数 */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">合成参数</p>
              {hasChanges && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetConfig}
                    className="h-7 text-xs"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    重置
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveConfig}
                    disabled={isSaving}
                    className="h-7 text-xs"
                  >
                    {isSaving ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Save className="h-3 w-3 mr-1" />
                    )}
                    保存
                  </Button>
                </div>
              )}
            </div>
            
            {/* 语速 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">语速</Label>
                <span className="text-sm font-medium text-violet-600">{speechRate.toFixed(1)}</span>
              </div>
              <Slider
                value={speechRate}
                onValueChange={(value) => setSpeechRate(value)}
                min={0.5}
                max={2.0}
                step={0.1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0.5 慢</span>
                <span>2.0 快</span>
              </div>
            </div>

            {/* 音调 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">音调</Label>
                <span className="text-sm font-medium text-violet-600">{pitchRate.toFixed(1)}</span>
              </div>
              <Slider
                value={pitchRate}
                onValueChange={(value) => setPitchRate(value)}
                min={0.5}
                max={2.0}
                step={0.1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0.5 低</span>
                <span>2.0 高</span>
              </div>
            </div>

            {/* 音量 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">音量</Label>
                <span className="text-sm font-medium text-violet-600">{volume}</span>
              </div>
              <Slider
                value={volume}
                onValueChange={(value) => setVolume(value)}
                min={0}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0 静音</span>
                <span>100 最大</span>
              </div>
            </div>

            {hasChanges && (
              <p className="text-xs text-amber-600 mt-2">
                💡 参数已修改，可先试听效果再保存。保存后下次对话生效。
              </p>
            )}
          </div>

          {/* 试听区域 */}
          <div className="space-y-3">
            <Label>试听</Label>
            <div className="flex gap-2">
              <Input
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                placeholder="输入试听文本..."
                maxLength={200}
              />
              <Button
                onClick={handlePreview}
                disabled={isPreviewing || !previewText.trim()}
              >
                {isPreviewing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
            </div>
            {previewUrl && (
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={togglePlay}
                >
                  {isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>
                <div className="flex-1 h-1 bg-slate-200 rounded-full" />
              </div>
            )}
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setBindDialogOpen(true)}
            >
              <Link2 className="h-4 w-4 mr-2" />
              更换绑定
            </Button>
            <Button
              variant="ghost"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={() => setUnbindDialogOpen(true)}
            >
              <Unlink className="h-4 w-4 mr-2" />
              解除绑定
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 绑定弹窗 */}
      <VoiceBindDialog
        open={bindDialogOpen}
        onOpenChange={setBindDialogOpen}
        identityId={identityId}
        sceneId={sceneId}
        sceneName={sceneName}
        currentVoiceId={voiceBinding.voice_card_id}
        onBindSuccess={handleBindSuccess}
      />

      {/* 解除绑定确认 */}
      <AlertDialog open={unbindDialogOpen} onOpenChange={setUnbindDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认解除绑定</AlertDialogTitle>
            <AlertDialogDescription>
              确定要解除「{sceneName}」与音色「{voiceBinding.voice_card_name}」的绑定吗？
              <br />
              <br />
              <span className="text-amber-600">
                ⚠️ 解除绑定后，如需发布该场景人格，需要重新绑定音色。
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unbinding}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnbind}
              className="bg-red-600 hover:bg-red-700"
              disabled={unbinding}
            >
              {unbinding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  处理中...
                </>
              ) : (
                '确认解除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
